import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import {
  Alert, Autocomplete, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, MenuItem, Paper, Select, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { bonusTrackerAPI } from '../../services/api';
import { BonusProgram, EligibilityRuleInfo, EmployeeOption, HISTORY_LABEL, HistoryClass, HistoryRow, HistoryRowDetail, employeeLabel, errText, money } from './bonusTypes';

interface Props {
  programs: BonusProgram[];
  /** A super user or the administration approver imports and classifies. */
  canClassify: boolean;
  canEnter: boolean;
  onOpenBonus: (recordId: string) => void;
  onChanged?: () => void;
}

const classColor = (c: HistoryClass): 'warning' | 'default' | 'success' | 'info' => (c === 'PENDING_RECONCILIATION' ? 'warning' : c === 'OWED' ? 'info' : c === 'PAID_HISTORICAL' ? 'success' : 'default');
const when = (v: string) => new Date(v.includes('T') ? v : `${v.replace(' ', 'T')}Z`).toLocaleString();
const CLASSES: HistoryClass[] = ['PENDING_RECONCILIATION', 'OWED', 'PAID_HISTORICAL', 'NOT_OWED'];

const HistoryPanel: React.FC<Props> = ({ programs, canClassify, canEnter, onOpenBonus, onChanged }) => {
  const [rows, setRows] = useState<HistoryRow[]>([]);
  const [counts, setCounts] = useState<Record<string, { rows: number; total: number }>>({});
  const [filter, setFilter] = useState<string>('PENDING_RECONCILIATION');
  const [search, setSearch] = useState('');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [importing, setImporting] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const d = await bonusTrackerAPI.historyRows({ classification: filter, search: search.trim() || undefined });
      setRows(d.rows);
      setCounts(d.counts);
    } catch (e) { setMessage({ type: 'error', text: errText(e) }); }
  }, [filter, search]);
  useEffect(() => { void load(); }, [load]);

  return (
    <Box>
      {message && <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>{message.text}</Alert>}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2} sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Rows of the old bonus sheets, kept exactly as they were (every cell and the notes). They are <b>not bonuses</b> and payroll can never receive them. A row the sheet calls paid is kept as history;
          every other row waits as <b>pending reconciliation</b> until an authorized person classifies it. Only a row classified as <b>owed</b> can be added to a bonus, where it needs every approval like any new bonus.
          Nothing is approved, cancelled or paid automatically.
        </Typography>
        {canClassify && <Button variant="contained" sx={{ flexShrink: 0 }} onClick={() => setImporting(true)}>Import old sheet</Button>}
      </Stack>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }} alignItems="center">
        {(['ALL', ...CLASSES] as string[]).map((c) => (
          <Chip key={c} clickable color={filter === c ? 'primary' : 'default'} variant={filter === c ? 'filled' : 'outlined'} onClick={() => setFilter(c)}
            label={c === 'ALL' ? `All (${Object.values(counts).reduce((s, x) => s + x.rows, 0)})` : `${HISTORY_LABEL[c as HistoryClass]} (${counts[c]?.rows || 0})`} />
        ))}
        <TextField size="small" label="Search agent, concept or note" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: 260, ml: 'auto !important' }} />
      </Stack>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Row</TableCell><TableCell>Agent</TableCell><TableCell>Week</TableCell><TableCell align="right">Amount</TableCell><TableCell>Concept</TableCell>
              <TableCell>Sheet status</TableCell><TableCell>Note</TableCell><TableCell>Classification</TableCell><TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} hover>
                <TableCell>{r.fileName}<Typography variant="caption" display="block" color="text.secondary">#{r.rowNumber}</Typography></TableCell>
                <TableCell>{r.agentText || '-'}{r.employeeName ? <Typography variant="caption" display="block" color="text.secondary">{r.employeeName}</Typography> : <Typography variant="caption" display="block" color="warning.main">no employee yet</Typography>}</TableCell>
                <TableCell>{r.weekText || '-'}</TableCell>
                <TableCell align="right">{r.amount == null ? '-' : money(r.amount, r.currency)}</TableCell>
                <TableCell sx={{ maxWidth: 220 }}>{r.concept || '-'}</TableCell>
                <TableCell>{r.statusText || <Typography variant="caption" color="text.secondary">blank</Typography>}{r.red && <Chip size="small" color="error" label="red row" sx={{ ml: 0.5 }} />}</TableCell>
                <TableCell sx={{ maxWidth: 200 }}>{r.notes || ''}</TableCell>
                <TableCell><Chip size="small" color={classColor(r.classification)} label={HISTORY_LABEL[r.classification]} />{r.promotedLineId && <Chip size="small" sx={{ ml: 0.5 }} label="In a bonus" />}</TableCell>
                <TableCell align="right"><Button size="small" onClick={() => setOpenId(r.id)}>Open</Button></TableCell>
              </TableRow>
            ))}
            {!rows.length && <TableRow><TableCell colSpan={9}><Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>No rows here.</Typography></TableCell></TableRow>}
          </TableBody>
        </Table>
      </TableContainer>
      {importing && <ImportDialog onClose={() => setImporting(false)} onDone={(text) => { setImporting(false); setMessage({ type: 'success', text }); void load(); onChanged?.(); }} />}
      {openId && <RowDialog id={openId} programs={programs} canClassify={canClassify} canEnter={canEnter} onClose={() => setOpenId(null)}
        onChanged={(text) => { if (text) setMessage({ type: 'success', text }); void load(); onChanged?.(); }} onOpenBonus={(id) => { setOpenId(null); onOpenBonus(id); }} />}
    </Box>
  );
};

/* ------------------------------ import a sheet ------------------------------ */

const FIELDS: Array<{ key: string; label: string; guess: RegExp; required?: boolean }> = [
  { key: 'agent', label: 'Agent', guess: /agent|agente|empleado|employee|nombre|name/i, required: true },
  { key: 'week', label: 'Week', guess: /semana|week|periodo|period/i },
  { key: 'amount', label: 'Amount', guess: /amount|monto|importe|total/i, required: true },
  { key: 'concept', label: 'Concept', guess: /concept|concepto|descrip|bonus|bono/i },
  { key: 'status', label: 'Status', guess: /status|estatus|estado/i },
  { key: 'dealerPay', label: 'Dealer pay date', guess: /dealer/i },
  { key: 'teksPay', label: 'TEKS pay date', guess: /teks/i },
  { key: 'notes', label: 'Notes', guess: /note|nota|coment|obs/i },
];

/** A cell filled with a strong red (the old sheets mark unpaid rows that way). Best effort: not every file keeps its colors. */
function looksRed(cell: any): boolean {
  const full = String(cell?.s?.fgColor?.rgb || cell?.s?.bgColor?.rgb || '');
  const rgb = full.length === 8 ? full.slice(2) : full; // 8 digits start with the opacity
  if (!/^[0-9a-f]{6}$/i.test(rgb)) return false;
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(rgb.slice(i, i + 2), 16));
  return r > 200 && g < 90 && b < 90;
}

interface Parsed { sheetNames: string[]; headers: string[]; data: string[][]; red: boolean[] }

const ImportDialog: React.FC<{ onClose: () => void; onDone: (text: string) => void }> = ({ onClose, onDone }) => {
  const [fileName, setFileName] = useState('');
  const [book, setBook] = useState<XLSX.WorkBook | null>(null);
  const [sheet, setSheet] = useState('');
  const [parsed, setParsed] = useState<Parsed | null>(null);
  const [map, setMap] = useState<Record<string, number>>({});
  const [currency, setCurrency] = useState('USD');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const readSheet = useCallback((wb: XLSX.WorkBook, name: string) => {
    const ws = wb.Sheets[name];
    const grid = XLSX.utils.sheet_to_json<string[]>(ws, { header: 1, raw: false, defval: '' }) as string[][];
    const headerIndex = Math.max(0, grid.findIndex((r) => r.some((c) => String(c).trim())));
    const headers = (grid[headerIndex] || []).map((h, i) => String(h || '').trim() || `Column ${XLSX.utils.encode_col(i)}`);
    const data = grid.slice(headerIndex + 1);
    const firstRow = ws['!ref'] ? XLSX.utils.decode_range(ws['!ref']).s.r : 0;
    const firstCol = ws['!ref'] ? XLSX.utils.decode_range(ws['!ref']).s.c : 0;
    const red = data.map((_, i) => {
      const rowIndex = firstRow + headerIndex + 1 + i;
      return headers.some((_h, c) => looksRed(ws[XLSX.utils.encode_cell({ r: rowIndex, c: firstCol + c })]));
    });
    const next: Record<string, number> = {};
    FIELDS.forEach((f) => { const idx = headers.findIndex((h, c) => f.guess.test(h) && !Object.values(next).includes(c)); if (idx >= 0) next[f.key] = idx; });
    setParsed({ sheetNames: wb.SheetNames, headers, data, red });
    setMap(next);
  }, []);

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    try {
      const wb = XLSX.read(await file.arrayBuffer(), { type: 'array', cellStyles: true });
      setFileName(file.name);
      setBook(wb);
      setSheet(wb.SheetNames[0]);
      readSheet(wb, wb.SheetNames[0]);
    } catch { setError('That file could not be read. Use an Excel or CSV file.'); }
  };

  const rows = useMemo(() => {
    if (!parsed) return [];
    return parsed.data.map((cells, i) => {
      const pick = (k: string) => (map[k] === undefined ? '' : cells[map[k]] ?? '');
      const raw: Record<string, string> = {};
      parsed.headers.forEach((h, c) => { raw[h] = String(cells[c] ?? ''); });
      return { agent: pick('agent'), week: pick('week'), amount: pick('amount'), concept: pick('concept'), status: pick('status'), dealerPay: pick('dealerPay'), teksPay: pick('teksPay'), notes: pick('notes'), red: parsed.red[i], raw };
    });
  }, [parsed, map]);

  const send = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await bonusTrackerAPI.importHistory({ fileName, sheetName: sheet, currency, rows });
      onDone(`${r.imported} row(s) imported: ${r.pending} waiting for reconciliation, ${r.paidInSheet} paid before. ${r.alreadyThere} already there, ${r.empty} empty. No bonus was created.`);
    } catch (e) { setError(errText(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>Import an old bonus sheet</DialogTitle>
      <DialogContent sx={{ display: 'grid', gap: 2, pt: '16px !important' }}>
        {error && <Alert severity="error">{error}</Alert>}
        <Box>
          <Button component="label" variant="outlined">
            Choose an Excel or CSV file
            <input hidden type="file" accept=".xlsx,.xls,.csv" onChange={(e) => { void onFile(e.target.files?.[0]); e.target.value = ''; }} />
          </Button>
          {fileName && <Typography variant="body2" sx={{ ml: 2, display: 'inline' }}>{fileName}</Typography>}
        </Box>
        {parsed && book && (
          <>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Sheet</InputLabel>
                <Select label="Sheet" value={sheet} onChange={(e) => { setSheet(e.target.value); readSheet(book, e.target.value); }}>
                  {parsed.sheetNames.map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField size="small" label="Currency" value={currency} onChange={(e) => setCurrency(e.target.value.toUpperCase().slice(0, 3))} sx={{ width: 110 }} />
              <Typography variant="body2" sx={{ alignSelf: 'center' }}>{rows.length} row(s) · {parsed.red.filter(Boolean).length} marked red</Typography>
            </Stack>
            <Typography variant="caption" color="text.secondary">Match each field to a column of the sheet. Every cell of every row is kept as it was, mapped or not. Red rows are detected from the cell colors when the file keeps them.</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr 1fr' }, gap: 1.5 }}>
              {FIELDS.map((f) => (
                <FormControl key={f.key} size="small" fullWidth>
                  <InputLabel>{f.label}{f.required ? ' *' : ''}</InputLabel>
                  <Select label={`${f.label}${f.required ? ' *' : ''}`} value={map[f.key] === undefined ? '' : map[f.key]} onChange={(e) => setMap((m) => { const n = { ...m }; if (e.target.value === '') delete n[f.key]; else n[f.key] = Number(e.target.value); return n; })}>
                    <MenuItem value="">(none)</MenuItem>
                    {parsed.headers.map((h, i) => <MenuItem key={i} value={i}>{h}</MenuItem>)}
                  </Select>
                </FormControl>
              ))}
            </Box>
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 220 }}>
              <Table size="small" stickyHeader>
                <TableHead><TableRow>{FIELDS.map((f) => <TableCell key={f.key}>{f.label}</TableCell>)}<TableCell>Red</TableCell></TableRow></TableHead>
                <TableBody>
                  {rows.slice(0, 6).map((r, i) => (
                    <TableRow key={i}>{FIELDS.map((f) => <TableCell key={f.key}>{String((r as any)[f.key] ?? '')}</TableCell>)}<TableCell>{r.red ? 'yes' : ''}</TableCell></TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={send} disabled={busy || !parsed || !rows.length || map.agent === undefined || map.amount === undefined}>Import {rows.length || ''} rows</Button>
      </DialogActions>
    </Dialog>
  );
};

/* ------------------------------ one row ------------------------------ */

const RowDialog: React.FC<{ id: string; programs: BonusProgram[]; canClassify: boolean; canEnter: boolean; onClose: () => void; onChanged: (text?: string) => void; onOpenBonus: (recordId: string) => void }> = ({ id, programs, canClassify, canEnter, onClose, onChanged, onOpenBonus }) => {
  const [r, setR] = useState<HistoryRowDetail | null>(null);
  const [target, setTarget] = useState<HistoryClass>('OWED');
  const [note, setNote] = useState('');
  const [options, setOptions] = useState<EmployeeOption[]>([]);
  const [programId, setProgramId] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [rules, setRules] = useState<EligibilityRuleInfo[]>([]);
  const [ruleId, setRuleId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const d: HistoryRowDetail = await bonusTrackerAPI.historyRow(id);
      setR(d);
      setPeriodEnd((p) => p || d.periodEnd || '');
    } catch (e) { setError(errText(e)); }
  }, [id]);
  useEffect(() => { void load(); }, [load]);
  useEffect(() => { void (async () => { try { setRules(await bonusTrackerAPI.eligibilityRules(false)); } catch { /* optional */ } })(); }, []);
  const search = useCallback(async (text: string) => { try { setOptions(await bonusTrackerAPI.employees(text, 'ALL', undefined, true)); } catch { /* typing still works */ } }, []);
  useEffect(() => { void search(''); }, [search]);

  const act = async (fn: () => Promise<any>, text: string) => {
    setBusy(true);
    setError(null);
    try { await fn(); await load(); setNote(''); onChanged(text); } catch (e) { setError(errText(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{r ? `${r.fileName} · row ${r.rowNumber}` : 'Historical row'}</DialogTitle>
      <DialogContent sx={{ display: 'grid', gap: 2, pt: '16px !important' }}>
        {error && <Alert severity="error">{error}</Alert>}
        {r && (
          <>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Chip color={classColor(r.classification)} label={HISTORY_LABEL[r.classification]} />
              {r.red && <Chip color="error" label="red row" />}
              <Typography variant="body2">{r.agentText || 'no agent'} · {r.amount == null ? 'no amount' : money(r.amount, r.currency)} · {r.weekText || 'no week'}</Typography>
            </Stack>
            {r.classificationNote && <Alert severity="info">{r.classifiedByName}: {r.classificationNote}</Alert>}
            {r.notes && <Alert severity="warning" icon={false}><b>Note in the sheet:</b> {r.notes}</Alert>}
            <Box>
              <Typography variant="subtitle2">The row exactly as it was in the sheet</Typography>
              <Table size="small">
                <TableBody>
                  {Object.entries(r.raw || {}).map(([k, v]) => (
                    <TableRow key={k}><TableCell sx={{ width: 220, fontWeight: 600 }}>{k}</TableCell><TableCell>{String(v ?? '')}</TableCell></TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>

            {canClassify && !r.promotedLineId && (
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Employee</Typography>
                <Autocomplete size="small" options={options} getOptionLabel={employeeLabel} isOptionEqualToValue={(a, b) => a.id === b.id} filterOptions={(x) => x}
                  value={options.find((o) => o.id === r.employeeId) || (r.employeeId && r.employeeName ? ({ id: r.employeeId, firstName: r.employeeName, lastName: '' } as EmployeeOption) : null)}
                  onInputChange={(_e, v, reason) => { if (reason === 'input') void search(v); }}
                  onChange={(_e, v) => { if (v) void act(() => bonusTrackerAPI.setHistoryEmployee(id, v.id), 'Employee saved.'); }}
                  renderInput={(params) => <TextField {...params} label={r.employeeId ? 'Employee' : `Pick the employee (the sheet says "${r.agentText || ''}")`} />} />
              </Paper>
            )}
            {canClassify && !r.promotedLineId && (
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Classify</Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <FormControl size="small" sx={{ minWidth: 220 }}>
                    <InputLabel>This row is</InputLabel>
                    <Select label="This row is" value={target} onChange={(e) => setTarget(e.target.value as HistoryClass)}>
                      {CLASSES.map((c) => <MenuItem key={c} value={c}>{HISTORY_LABEL[c]}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <TextField size="small" fullWidth required label="Note (what you checked, or where it was confirmed)" value={note} onChange={(e) => setNote(e.target.value)} />
                  <Button variant="contained" disabled={busy || note.trim().length < 3 || target === r.classification} onClick={() => void act(() => bonusTrackerAPI.classifyHistory(id, target, note), 'Row classified.')}>Save</Button>
                </Stack>
              </Paper>
            )}
            {r.classification === 'OWED' && canEnter && (
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Add to a bonus</Typography>
                {r.promotedLineId && r.promotedRecordId ? (
                  <Button size="small" onClick={() => onOpenBonus(r.promotedRecordId!)}>Open the bonus it is in</Button>
                ) : (
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    <FormControl size="small" fullWidth>
                      <InputLabel>Program</InputLabel>
                      <Select label="Program" value={programId} onChange={(e) => setProgramId(e.target.value)}>
                        {programs.map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
                      </Select>
                    </FormControl>
                    <TextField size="small" type="date" label="Bonus ending" InputLabelProps={{ shrink: true }} value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} />
                    <Button variant="outlined" sx={{ whiteSpace: 'nowrap' }} disabled={busy || !programId || !periodEnd} onClick={() => void act(() => bonusTrackerAPI.promoteHistory(id, programId, periodEnd), 'Added to a bonus. It needs every approval.')}>Add to bonus</Button>
                  </Stack>
                )}
                <Typography variant="caption" color="text.secondary">It enters with no approval at all and needs all four, like any new bonus.</Typography>
              </Paper>
            )}
            {r.notes && canEnter && (
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Review the note (write-up, suspension, absence)</Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Eligibility reason</InputLabel>
                    <Select label="Eligibility reason" value={ruleId} onChange={(e) => setRuleId(e.target.value)}>
                      {rules.map((x) => <MenuItem key={x.id} value={x.id}>{x.name}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <Button variant="outlined" sx={{ whiteSpace: 'nowrap' }} disabled={busy || !ruleId || !r.employeeId} onClick={() => void act(() => bonusTrackerAPI.historyEligibility(id, ruleId), 'Eligibility review opened. It disqualifies nobody until it is decided.')}>Open review</Button>
                </Stack>
              </Paper>
            )}
            <Box>
              <Typography variant="subtitle2">History</Typography>
              {r.events.map((e, i) => <Typography key={i} variant="caption" display="block" color="text.secondary">{when(e.createdAt)} · {e.actorName} · {e.action.replace(/_/g, ' ').toLowerCase()}{e.note ? ` · ${e.note}` : ''}</Typography>)}
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
};

export default HistoryPanel;
